import { forwardRef, useEffect, useRef, useState } from 'react';
import axios from "axios";
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

// Async, server-side search for existing Person records by last name or phone number.
// Replaces the old pattern of paginating through the entire Person list client-side.
const ExistingPersonSearch = forwardRef(({ id, params = {}, onSelect, onError, minLength = 3, delay = 300, placeholder, disabled, className, style, inputProps, emptyLabel = "No matches found. Please fill out the form below." }, ref) => {

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const requestId = useRef(0);
  const source = useRef(null);
  const unmounted = useRef(false);

  useEffect(() => {
    return () => {
      unmounted.current = true;
      if (source.current) {
        source.current.cancel();
      }
    };
  }, []);

  const handleSearch = async (query) => {
    if (source.current) {
      source.current.cancel();
    }
    source.current = axios.CancelToken.source();
    const thisRequestId = ++requestId.current;

    setIsLoading(true);
    let owners = [];
    let nextUrl = '/people/api/person/';
    do {
      const response = await axios.get(nextUrl, {
        params: { ...params, name_or_phone: query, light: true, page_size: 25 }
      })
      .catch(error => {
        if (error.response) {
          if (onError) {
            onError(error);
          }
        }
      });

      owners.push(...response.data.results.map(person => ({
        id: person.id,
        label: person.first_name + ' ' + person.last_name + ' - ' + person.display_phone,
        person,
      })));
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/people/' + response.data.next.split('/people/')[1];
      }
    } while(nextUrl != null)

    if (!unmounted.current && thisRequestId === requestId.current) {
      setOptions(owners);
      setIsLoading(false);
    }
  };

  return (
    <AsyncTypeahead
      id={id}
      ref={ref}
      isLoading={isLoading}
      minLength={minLength}
      delay={delay}
      useCache={false}
      filterBy={() => true}
      labelKey="label"
      onSearch={handleSearch}
      options={options}
      onChange={(selected) => onSelect(selected.length ? selected[0].person : null)}
      placeholder={placeholder || (isLoading ? "Searching..." : "Search by last name or phone number...")}
      disabled={disabled}
      className={className}
      style={style}
      inputProps={inputProps}
      emptyLabel={emptyLabel}
    />
  );
});

export default ExistingPersonSearch;
